/**
 * HUDDL HQ design tokens — Phase K.0.
 * Brand colors are frozen. All UI must consume these tokens (never hardcode).
 */

export const colors = {
  primary: "#003865",
  accent: "#96D700",
  verifiedBlack: "#1F1F21",
  white: "#FFFFFF",
  gray: {
    50: "#F7F8F9",
    100: "#EEF1F4",
    200: "#DCE3EA",
    300: "#C0CBD6",
    400: "#8FA0B0",
    500: "#66788A",
    600: "#4D5E6E",
    700: "#3A4855",
    800: "#2A343E",
    900: "#1C242C",
  },
  semantic: {
    success: "#2E7D32",
    warning: "#B26A00",
    danger: "#C62828",
    info: "#1565C0",
  },
  /** Derived surfaces from brand + neutrals */
  surface: {
    canvas: "#F7F8F9",
    raised: "#FFFFFF",
    sunken: "#EEF1F4",
    overlay: "rgba(31, 31, 33, 0.48)",
    primarySubtle: "rgba(0, 56, 101, 0.08)",
    accentSubtle: "rgba(150, 215, 0, 0.16)",
  },
  text: {
    primary: "#1F1F21",
    secondary: "#4D5E6E",
    muted: "#66788A",
    inverse: "#FFFFFF",
    link: "#003865",
    onPrimary: "#FFFFFF",
    onAccent: "#1F1F21",
  },
  border: {
    subtle: "#DCE3EA",
    default: "#C0CBD6",
    strong: "#8FA0B0",
    focus: "#003865",
  },
} as const;

/** Spacing scale (px): 4, 8, 12, 16, 24, 36 */
export const spacing = {
  4: "4px",
  8: "8px",
  12: "12px",
  16: "16px",
  24: "24px",
  36: "36px",
  xs: "4px",
  sm: "8px",
  md: "12px",
  lg: "16px",
  xl: "24px",
  "2xl": "36px",
} as const;

/** Radius scale (px): 10, 16, 22, 30, 999 */
export const radius = {
  10: "10px",
  16: "16px",
  22: "22px",
  30: "30px",
  999: "999px",
  sm: "10px",
  md: "16px",
  lg: "22px",
  xl: "30px",
  full: "999px",
} as const;

/**
 * Typography — Inter only.
 * Named roles from approved brand system.
 */
export const typography = {
  fontFamily: {
    sans: '"Inter", system-ui, -apple-system, "Segoe UI", sans-serif',
  },
  display: {
    fontSize: "40px",
    lineHeight: "48px",
    fontWeight: 700,
    letterSpacing: "-0.02em",
  },
  hero: {
    fontSize: "32px",
    lineHeight: "40px",
    fontWeight: 700,
    letterSpacing: "-0.02em",
  },
  h1: {
    fontSize: "28px",
    lineHeight: "36px",
    fontWeight: 600,
    letterSpacing: "-0.015em",
  },
  h2: {
    fontSize: "22px",
    lineHeight: "28px",
    fontWeight: 600,
    letterSpacing: "-0.01em",
  },
  title: {
    fontSize: "18px",
    lineHeight: "24px",
    fontWeight: 600,
    letterSpacing: "-0.01em",
  },
  body: {
    fontSize: "14px",
    lineHeight: "20px",
    fontWeight: 400,
    letterSpacing: "0",
  },
  bodyStrong: {
    fontSize: "14px",
    lineHeight: "20px",
    fontWeight: 600,
    letterSpacing: "0",
  },
  small: {
    fontSize: "13px",
    lineHeight: "18px",
    fontWeight: 400,
    letterSpacing: "0",
  },
  label: {
    fontSize: "13px",
    lineHeight: "16px",
    fontWeight: 500,
    letterSpacing: "0.01em",
  },
  caption: {
    fontSize: "12px",
    lineHeight: "16px",
    fontWeight: 400,
    letterSpacing: "0.01em",
  },
  overline: {
    fontSize: "11px",
    lineHeight: "14px",
    fontWeight: 600,
    letterSpacing: "0.06em",
    textTransform: "uppercase" as const,
  },
} as const;

export const shadows = {
  none: "none",
  sm: "0 1px 2px rgba(31, 31, 33, 0.06)",
  md: "0 4px 12px rgba(31, 31, 33, 0.08)",
  lg: "0 12px 32px rgba(31, 31, 33, 0.12)",
  focus: "0 0 0 3px rgba(0, 56, 101, 0.28)",
  accentFocus: "0 0 0 3px rgba(150, 215, 0, 0.45)",
} as const;

export const zIndex = {
  base: 0,
  sticky: 100,
  dropdown: 200,
  drawer: 300,
  modal: 400,
  toast: 500,
  tooltip: 600,
} as const;

export const motion = {
  duration: {
    fast: "120ms",
    normal: "200ms",
    slow: "320ms",
  },
  easing: {
    standard: "cubic-bezier(0.2, 0, 0, 1)",
    emphasized: "cubic-bezier(0.2, 0, 0, 1)",
  },
} as const;

export const layout = {
  sidebarWidth: "256px",
  sidebarCollapsedWidth: "72px",
  topNavHeight: "56px",
  contentMaxWidth: "1280px",
} as const;

export const tokens = {
  colors,
  spacing,
  radius,
  typography,
  shadows,
  zIndex,
  motion,
  layout,
} as const;

export type HuddlTokens = typeof tokens;
export type TypographyRole = keyof Omit<typeof typography, "fontFamily">;
